import './styles.scoped.css'
import {useEffect, useRef, useState} from 'react'
import {Box, Tabs, Tab, Paper, Typography, Avatar, Stack, Button} from '@mui/material'
import {ChevronRightRounded, DateRange, AccessTime} from '@mui/icons-material'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime} from '../../../../utils/helpers'
import {format} from 'date-fns'
import {useMobile} from '../../../../hooks'
import {dashboardApi, scheduleApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {VisitCardsSkeleton} from '../DashboardSkeletons'
import {ChangeCaregiver, RescheduleVisits} from '../../../../components'

export default () => {
    const {mobile} = useMobile()
    const {setMessageBox, branchId, dashboardDate, dashboardRefresh} = useGlobalStates()
    const [items, setItems] = useState(null)
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const [openChangeCaregiver, setOpenChangeCaregiver] = useState(false)
    const [openRescheduleVisits, setOpenRescheduleVisits] = useState(false)
    const [visit, setVisit] = useState(null)
    const [visitLoading, setVisitLoading] = useState(false)
    const visitRequestId = useRef(0)

    // A new day, branch or Refresh replaces the list with the skeleton.
    useEffect(() => {
        setSkeleton(true)
        setItems(null)
    }, [branchId, dashboardDate, dashboardRefresh])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (branchId && dashboardDate) {
                const id = ++requestId.current
                dashboardApi
                    .getTodaysScheduleOverview(branchId, {date: format(dashboardDate, 'yyyy-MM-dd'), page_size: 100, tab: 'unassigned'})
                    .then(
                        ({
                            data: {
                                data: {visits},
                            },
                        }) => {
                            if (id !== requestId.current) return
                            setItems(visits)
                            setSkeleton(false)
                        },
                    )
                    .catch(error => {
                        if (id !== requestId.current) return
                        const {title, description} = parseError(error)
                        if (description) {
                            setMessageBox({status: 'error', title, description})
                        }
                        setSkeleton(false)
                    })
            }
        }, 500)
        return () => clearTimeout(timeout)
    }, [branchId, dashboardDate, dashboardRefresh])

    const getScheduleVisit = (scheduleId, visitId) => {
        if (branchId) {
            const id = ++visitRequestId.current
            setVisitLoading(true)
            scheduleApi
                .getScheduleVisit(branchId, scheduleId, visitId)
                .then(({data}) => {
                    if (id !== visitRequestId.current) return
                    setVisitLoading(false)
                    setVisit(data)
                })
                .catch(error => {
                    if (id !== visitRequestId.current) return
                    setVisitLoading(false)
                    setOpenChangeCaregiver(false)
                    setOpenRescheduleVisits(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }

    const loadData = () => {}

    const changeCaregiver = item => {
        setOpenChangeCaregiver(true)
        setVisit(null)
        getScheduleVisit(item?.schedule_id, item?.id)
    }

    const reschedule = item => {
        setOpenRescheduleVisits(true)
        setVisit(null)
        getScheduleVisit(item?.schedule_id, item?.id)
    }

    return (
        <div className="container">
            {skeleton && <VisitCardsSkeleton mobile={mobile} people={1} buttons={[110, 145]} mobileButtons={[2]} />}
            {items?.map((item, index) => (
                <div key={index}>
                    <br />
                    {mobile ? (
                        <div className="card">
                            <Typography variant="h8" color="text.primary">
                                {item?.service_name}
                            </Typography>
                            <br />
                            <br />
                            <div className="datetime">
                                <div className="row-content">
                                    <Typography variant="body3" color="text.secondary">
                                        <DateRange fontSize="small" />
                                    </Typography>
                                    <Typography variant="body3" color="text.secondary">
                                        {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                    </Typography>
                                </div>
                                <Typography variant="body3" color="text.secondary">
                                    |
                                </Typography>
                                <div className="row-content">
                                    <Typography variant="body3" color="text.secondary">
                                        <AccessTime fontSize="small" />
                                    </Typography>
                                    <Typography variant="body3" color="text.secondary">
                                        {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                    </Typography>
                                </div>
                            </div>
                            <br />
                            <Typography variant="caption" color="primary">
                                CLIENT
                            </Typography>
                            <div className="people-card">
                                {item?.client?.avatar_url ? (
                                    <Avatar src={item?.client?.avatar_url} style={{width: 40, height: 40}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.client?.name),
                                            width: 40,
                                            height: 40,
                                            fontSize: 20,
                                        }}>
                                        {getInitials(item?.client?.name)}
                                    </Avatar>
                                )}
                                <div className="people-profile-card">
                                    <Typography variant="h8" color="text.primary" className="name">
                                        {item?.client?.name}
                                    </Typography>
                                    <Typography variant="body3" color="text.secondary" className="name">
                                        {item?.client?.email}
                                    </Typography>
                                </div>
                            </div>
                            <br />
                            <div className="actions">
                                <Button variant="outlined" size="small" fullWidth color="error" onClick={() => reschedule(item)}>
                                    Reschedule
                                </Button>
                                <Button variant="contained" size="small" fullWidth onClick={() => changeCaregiver(item)}>
                                    Assign Caregiver
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="card">
                            <div className="card-top">
                                <Typography variant="h8" color="text.primary">
                                    {item?.service_name}
                                </Typography>
                                <div className="datetime">
                                    <div className="row-content">
                                        <Typography variant="body3" color="text.secondary">
                                            <DateRange fontSize="small" />
                                        </Typography>
                                        <Typography variant="body3" color="text.secondary">
                                            {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                        </Typography>
                                    </div>
                                    <Typography variant="body3" color="text.secondary">
                                        |
                                    </Typography>
                                    <div className="row-content">
                                        <Typography variant="body3" color="text.secondary">
                                            <AccessTime fontSize="small" />
                                        </Typography>
                                        <Typography variant="body3" color="text.secondary">
                                            {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                            <div className="card-bottom">
                                <div>
                                    <Typography variant="caption" color="primary">
                                        CLIENT
                                    </Typography>
                                    <div className="people-card">
                                        {item?.client?.avatar_url ? (
                                            <Avatar src={item?.client?.avatar_url} style={{width: 40, height: 40}} />
                                        ) : (
                                            <Avatar
                                                style={{
                                                    backgroundColor: getColorFromName(item?.client?.name),
                                                    width: 40,
                                                    height: 40,
                                                    fontSize: 20,
                                                }}>
                                                {getInitials(item?.client?.name)}
                                            </Avatar>
                                        )}
                                        <div className="people-profile-card">
                                            <Typography variant="h8" color="text.primary" className="name">
                                                {item?.client?.name}
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary" className="name">
                                                {item?.client?.email}
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                                <div className="actions">
                                    <Button variant="outlined" color="error" onClick={() => reschedule(item)}>
                                        Reschedule
                                    </Button>
                                    <Button variant="contained" onClick={() => changeCaregiver(item)}>
                                        Assign Caregiver
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ))}
            <ChangeCaregiver open={openChangeCaregiver} setOpen={setOpenChangeCaregiver} loadData={loadData} items={[visit]} loading={visitLoading} />
            <RescheduleVisits
                open={openRescheduleVisits}
                setOpen={setOpenRescheduleVisits}
                loadData={loadData}
                items={[visit]}
                loading={visitLoading}
            />
        </div>
    )
}
